/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/display-name */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { parseDate, currency, setSavedFilter } from '@global/helpers';
import { showPopup } from '@/store/modal';
import {
  View,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import Colors from '@global/colors';
import { isWeb } from '@global/screens';
import { buttonPrimary, buttonRounded } from '@/assets/styles/buttonStyles';
import SearchTable from '@components/table/SearchTable';
import Pagination from '@components/table/Pagination';
import SociollaText from '@components/sociolla/SociollaText';
import { NoTranslate } from '@components/shared/TranslationContext';
import Image from '@components/global/Image';
import Input from '@components/form/Input';
import Button from '@components/form/Button';
import CheckBox from '@components/form/CheckBox';
import StatusBadge from '@components/global/StatusBadge';

import store from '@/store';
import {
  filterAdded,
  paginationAdded,
  sortChanged,
  paramsReseted,
  selectParams,
  getCurrentParams,
} from '@/store/params';

const trash = {
  width: 14,
  height: 18,
};

const iconSize = 16;

const TableSearch = React.memo(
  (props) => {
    const {
      testID,
      showSearch,
      searchKeyword,
      searchKey,
      searchWidth,
      searchBorder,
      searchPlaceholder,
      setSearchKeyword,
      onSearch,
      searchForOptions,
      searchForKeyword,
      setSearchForKeyword,
      filterName,
    } = props;
    return showSearch ? (
      <View style={{ marginRight: 10 }}>
        <SearchTable
          testID={testID}
          value={searchKeyword}
          onSearch={async (val, forValue) => {
            await setSearchKeyword(val);
            await setSearchForKeyword(forValue);
            onSearch(val, forValue);
          }}
          searchKey={searchKey}
          width={searchWidth}
          withBorder={searchBorder ? true : false}
          searchPlaceholder={
            searchPlaceholder ? searchPlaceholder : 'Search something...'
          }
          searchForOptions={searchForOptions}
          valueFor={searchForKeyword}
          filterName={filterName}
        />
      </View>
    ) : (
      false
    );
  },
  (prev, next) => prev == next,
);

const TableFilter = React.memo(
  (props) => {
    const { testID, name, showFilter, hasFilter, dispatch } = props;
    return showFilter ? (
      <View>
        <Button
          testID={`${testID}-filter-button`}
          buttonStyle={{
            ...buttonPrimary,
            ...buttonRounded,
            width: 90,
          }}
          title={
            <View style={{ flexDirection: 'row' }}>
              {hasFilter ? (
                <Image
                  style={{ width: 14, height: 15, marginRight: 10 }}
                  width={14}
                  height={15}
                  svg={true}
                  source={require('@assets/img/icons/check-circle-white.svg')}
                />
              ) : (
                <Image
                  style={{ width: 14, height: 15, marginRight: 10 }}
                  width={14}
                  height={15}
                  svg={true}
                  source={require('@assets/img/icons/filter-white.svg')}
                />
              )}
              <SociollaText
                type="medium"
                style={{ fontSize: 14, color: Colors.white }}>
                Filter
              </SociollaText>
            </View>
          }
          onPress={() =>
            dispatch(showPopup({ name: 'showFilter', value: name }))
          }
        />
      </View>
    ) : (
      false
    );
  },
  (prev, next) => prev == next,
);

const TopPagination = React.memo(
  (props) => {
    const {
      testID,
      showPagination,
      bottomPaginationOnly,
      totalData,
      skip,
      limit,
      onSetSkip,
      onSetLimit,
    } = props;

    return showPagination && !bottomPaginationOnly ? (
      <Pagination
        testID={testID}
        totalData={totalData}
        limit={limit}
        skip={skip}
        onSetSkip={(val) => onSetSkip(val)}
        onSetLimit={onSetLimit ? (val) => onSetLimit(val) : null}
      />
    ) : (
      false
    );
  },
  (prev, next) => prev == next,
);

const TableEmpty = React.memo(
  (props) => {
    const { hasFilter, showResetFilter, onResetFilter } = props;

    return hasFilter && !showResetFilter ? (
      <View
        style={{
          minHeight: 150,
          justifyContent: 'center',
          alignItems: 'center',
        }}>
        <SociollaText style={{ fontSize: 13, marginBottom: 15 }}>
          No data found
        </SociollaText>
        <Button
          type="default"
          rounded
          buttonStyle={{ width: 120 }}
          title={
            <SociollaText type="medium" style={{ fontSize: 13 }}>
              Reset Filter
            </SociollaText>
          }
          onPress={() => onResetFilter()}
        />
      </View>
    ) : (
      <SociollaText style={{ fontSize: 13 }}>No data found</SociollaText>
    );
  },
  (prev, next) => prev == next,
);

const TableTop = React.memo(
  (props) => {
    const {
      testID,
      name,
      customTopSection,
      noTopSection,
      showSearch,
      searchKey,
      searchKeyword,
      searchWidth,
      searchBorder,
      searchPlaceholder,
      showFilter,
      hasFilter,
      moreInlineFilter,
      showPagination,
      bottomPaginationOnly,
      totalData,
      skip,
      limit,
      dispatch,
      setSearchKeyword,
      onSearch,
      onSetSkip,
      onSetLimit,
      searchForOptions,
      searchForKeyword,
      setSearchForKeyword,
      overflowX,
      filterName,
    } = props;

    return customTopSection ? (
      customTopSection
    ) : !noTopSection ? (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 100,
          elevation: 100,
          ...(overflowX
            ? {
                position: 'sticky',
                left: 0,
                zIndex: 101,
                elevation: 101,
              }
            : {}),
        }}>
        <View style={{ flexDirection: 'row' }}>
          {/* Search Container */}
          <TableSearch
            testID={`${testID}-search`}
            showSearch={showSearch}
            searchKeyword={searchKeyword}
            searchKey={searchKey}
            searchWidth={searchWidth}
            searchBorder={searchBorder}
            searchPlaceholder={searchPlaceholder}
            setSearchKeyword={setSearchKeyword}
            onSearch={onSearch}
            searchForOptions={searchForOptions}
            searchForKeyword={searchForKeyword}
            setSearchForKeyword={setSearchForKeyword}
            filterName={filterName}
          />
          {/* Filter Button Container */}
          <TableFilter
            testID={`${testID}-filter`}
            name={name}
            showFilter={showFilter}
            hasFilter={hasFilter}
            dispatch={dispatch}
          />
          {/* More Custom Component */}
          {moreInlineFilter ? moreInlineFilter : false}
        </View>
        {/* Top Pagination Component */}
        <View style={{ marginLeft: 'auto' }}>
          <TopPagination
            testID={`${testID}-top-pagination`}
            showPagination={showPagination}
            bottomPaginationOnly={bottomPaginationOnly}
            totalData={totalData}
            skip={skip}
            limit={limit}
            onSetSkip={onSetSkip}
            onSetLimit={onSetLimit}
          />
        </View>
      </View>
    ) : (
      false
    );
  },
  (prev, next) => prev == next,
);

const TableHeader = React.memo(
  (props) => {
    const {
      isTop = false,
      finalStructures,
      hideColumn,
      hasNoHide,
      data,
      checkbox,
      withSort,
      hasSort,
      isShowHide,
      onCheckbox,
      onMapAction,
      stickyLeftByKey,
      stickyWidthByKey,
    } = props;

    // Table headers are excluded from translation (same as filter/export
    // popups), so wrap them regardless of which screen's Table this is.
    return (
      <NoTranslate>
        {finalStructures.map((item, idx) => {
          if (!isShowHide(item.key)) {
            return false;
          }

          const stickyLeft = stickyLeftByKey?.[item.key];
          const stickyWidth = stickyWidthByKey?.[item.key];
          const isSticky = typeof stickyLeft == 'number';

          return (
            <th
              key={`structure-${idx}`}
              style={{
                padding: '12px 15px',
                textAlign: 'left',
                ...(item.headStyle ? item.headStyle : {}),
                borderLeft: `1px solid ${
                  idx == 0 ? Colors.blue3 : Colors.grey0
                }`,
                borderRight: `1px solid ${
                  idx == finalStructures.length - 1 &&
                  (!(hideColumn && hideColumn.length) || hasNoHide)
                    ? Colors.blue3
                    : Colors.grey0
                }`,
                borderTop: `1px solid ${Colors.blue3}`,
                borderBottom: `1px solid ${
                  isTop ? Colors.grey0 : Colors.blue3
                }`,
                ...(item.type == 'checkbox' ? { width: 46 } : {}),
                ...(isSticky
                  ? {
                      position: 'sticky',
                      left: stickyLeft,
                      boxSizing: 'border-box',
                      width: stickyWidth,
                      minWidth: stickyWidth,
                      maxWidth: stickyWidth,
                      borderRight: `1px solid ${Colors.grey0}`,
                      boxShadow: `inset -1px 0 0 ${Colors.grey0}`,
                      zIndex: isTop ? 6 : 5,
                      backgroundColor: Colors.blue3,
                    }
                  : {}),
              }}
              colSpan={item.colSpan ? item.colSpan : 1}
              rowSpan={item.rowSpan ? item.rowSpan : 1}>
              {item.type == 'checkbox' ? (
                <View key={`all-checkbox-${idx}`}>
                  <CheckBox
                    value="all"
                    checked={
                      ((data && data.length) || 0) > 0 &&
                      checkbox.length == (data && data.length)
                    }
                    isWhite={true}
                    checkboxStyle={{
                      width: 16,
                      height: 16,
                      borderColor: Colors.white,
                    }}
                    checkboxCheckedStyle={{ backgroundColor: Colors.white }}
                    onChange={() => onCheckbox(item, -1, true)}
                  />
                </View>
              ) : withSort &&
                !['action', 'checkbox'].includes(item.type) &&
                !item.noSort ? (
                <TouchableOpacity
                  activeOpacity={0.6}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onPress={() => onMapAction(item, idx, { name: 'sort' })}>
                  <SociollaText
                    type="medium"
                    style={{
                      fontSize: 14,
                      color: Colors.white,
                      marginRight: 10,
                    }}>
                    {item.head}
                  </SociollaText>
                  {hasSort && hasSort.replace('-', '') == item.body ? (
                    <Image
                      style={{
                        width: 10,
                        height: 5,
                        transform: [
                          {
                            rotate:
                              hasSort && hasSort.indexOf('-') > -1
                                ? '180deg'
                                : '0deg',
                          },
                        ],
                      }}
                      width={10}
                      height={5}
                      svg={true}
                      source={require('@assets/img/icons/arrow-down-white.svg')}
                    />
                  ) : (
                    false
                  )}
                </TouchableOpacity>
              ) : (
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.white }}>
                  {item.head}
                </SociollaText>
              )}
            </th>
          );
        })}
      </NoTranslate>
    );
  },
  (prev, next) => prev == next,
);

const TableHideColumn = React.memo(
  (props) => {
    const {
      hideColumn,
      hasNoHide,
      showMoreField,
      setShowMoreField,
      showField,
    } = props;

    return (hideColumn && hideColumn.length) || !hasNoHide ? (
      <th
        style={{
          width: '15px',
          padding: '15px',
          borderLeft: `1px solid ${Colors.grey0}`,
          borderRight: `1px solid ${Colors.blue3}`,
          borderTop: `1px solid ${Colors.blue3}`,
          borderBottom: `1px solid ${Colors.blue3}`,
        }}>
        <View style={{ position: 'relative', zIndex: 1, elevation: 1 }}>
          <TouchableOpacity
            activeOpacity={0.6}
            style={{
              justifyContent: 'center',
              alignItems: 'center',
            }}
            onPress={() => setShowMoreField(!showMoreField)}>
            <Image
              style={{ width: 5, height: 20 }}
              width={5}
              height={20}
              svg={true}
              source={require('@assets/img/icons/more-vertical-white.svg')}
            />
          </TouchableOpacity>
          {showMoreField ? (
            <TouchableOpacity
              activeOpacity={0.6}
              style={{
                width: 230,
                position: 'absolute',
                right: -16,
                top: 35,
                backgroundColor: Colors.white,
                zIndex: 2,
                elevation: 2,
              }}>
              {hideColumn.map((item, idx) => (
                <View
                  style={{
                    paddingVertical: 12,
                    paddingHorizontal: 15,
                    borderBottomColor: Colors.grey0,
                    borderBottomWidth: 1,
                  }}
                  key={`more-field-${idx}`}>
                  <CheckBox
                    value={item.key}
                    checked={item.show}
                    checkboxStyle={{ width: 16, height: 16 }}
                    onChange={() => showField(idx)}>
                    <SociollaText style={{ lineHeight: 18 }}>
                      {item.name}
                    </SociollaText>
                  </CheckBox>
                </View>
              ))}
            </TouchableOpacity>
          ) : (
            false
          )}
        </View>
      </th>
    ) : (
      false
    );
  },
  (prev, next) => prev == next,
);

const TableList = React.memo(
  (props) => {
    const {
      testID,
      loading,
      hideColumn,
      hasNoHide,
      finalStructures,
      data,
      headerComponent,
      headerComponentElement,
      slotComponent,
      noRowColor,
      rowBottomBorder,
      checkbox,
      hasFilter,
      showResetFilter,
      onClickRow,
      isShowHide,
      getItem,
      onCheckbox,
      onMapAction,
      onResetFilter,
      isEdit,
      ChangeBudgetSample,
      stickyLeftByKey,
      stickyWidthByKey,
      disablePointer,
    } = props;

    const [editableCell, setEditableCell] = useState(null);
    const [editableValue, setEditableValue] = useState('');

    const isEditableCell = (rowIndex, columnKey) =>
      Boolean(
        editableCell &&
          editableCell.rowIndex == rowIndex &&
          editableCell.columnKey == columnKey,
      );

    const activateEditableCell = ({ event, rowData, rowIndex, column }) => {
      if (!(column && column.editable)) {
        return;
      }

      if (event && event.stopPropagation) {
        event.stopPropagation();
      }

      const value = getItem(rowData, column.body);
      setEditableCell({ rowIndex, columnKey: column.key });
      setEditableValue(value || value == 0 ? value.toString() : '');
    };

    const onEditableValueChange = ({ rowData, rowIndex, column, value }) => {
      setEditableValue(value);
      onMapAction(rowData, rowIndex, {
        name: 'column_editable',
        key: column.key,
        body: column.body,
        value: value,
      });
    };

    const deactivateEditableCell = () => {
      setEditableCell(null);
    };

    const shouldRenderValue = (value, structure) => {
      if (structure?.checkNullUndefinedOnly) {
        return value !== null && value !== undefined;
      }

      return Boolean(value);
    };

    const renderCellText = ({
      rowData,
      structure,
      textStyle,
      formatter = (value) => value,
    }) => {
      const value = getItem(rowData, structure.body);
      return (
        <SociollaText style={textStyle}>
          {shouldRenderValue(value, structure) ? formatter(value) : '-'}
        </SociollaText>
      );
    };

    return loading ? (
      <tr>
        <td colSpan={finalStructures.length} style={{ padding: 15 }}>
          <ActivityIndicator size="large" color={Colors.blue0} />
        </td>
      </tr>
    ) : data && data.length ? (
      <>
        {headerComponentElement ? (
          <tr style={{ position: 'relative', zIndex: 1, elevation: 1 }}>
            <td style={{ maxWidth: 200 }} colSpan={headerComponent.colSpan}>
              {headerComponent.component}
            </td>
          </tr>
        ) : (
          false
        )}
        {data.map((item, idx) => {
          const rowBackgroundColor =
            !noRowColor && idx % 2 == 0 ? Colors.grey4 : Colors.white;

          return (
            <tr
              key={`data-${idx}`}
              data-testid={testID ? `${testID}-row-${idx}` : `table-row-${idx}`}
              style={{
                backgroundColor: rowBackgroundColor,
                cursor: disablePointer ? 'auto' : 'pointer',
                ...(item.style ? item.style : {}),
              }}
              onClick={() => onClickRow({ data: item, index: idx })}>
              {finalStructures.map((child, childIdx) => {
                // console.log(slotComponent[child.body])
                const stickyLeft = stickyLeftByKey?.[child.key];
                const stickyWidth = stickyWidthByKey?.[child.key];
                const isSticky = typeof stickyLeft == 'number';
                const textStyle = {
                  ...(child.textStyle ? child.textStyle : {}),
                  ...(item.textStyle ? item.textStyle : {}),
                };
                return isShowHide(child.key) ? (
                  <td
                    key={`data-${childIdx}`}
                    style={{
                      padding: '12px 15px',
                      border: 'none',
                      position: 'relative',
                      ...(child.type == 'checkbox' ? { width: 46 } : {}),
                      ...(child.maxWidth ? { maxWidth: child.maxWidth } : {}),
                      ...(isSticky
                        ? {
                            position: 'sticky',
                            left: stickyLeft,
                            boxSizing: 'border-box',
                            width: stickyWidth,
                            minWidth: stickyWidth,
                            maxWidth: stickyWidth,
                            borderRight: `1px solid ${Colors.grey0}`,
                            boxShadow: `inset -1px 0 0 ${Colors.grey0}`,
                            backgroundColor: rowBackgroundColor,
                            zIndex: 2,
                          }
                        : {}),
                      ...(rowBottomBorder
                        ? {
                            borderBottomColor: Colors.grey0,
                            borderBottomWidth: 1,
                            borderBottomStyle: 'solid',
                          }
                        : {}),
                    }}
                    onDoubleClick={(event) =>
                      activateEditableCell({
                        event,
                        rowData: item,
                        rowIndex: idx,
                        column: child,
                      })
                    }>
                    <span
                      id={item.elementId ? item.elementId : Math.random()}
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: item.offset ? item.offset : 0,
                        zIndex: -1,
                        elevation: -1,
                      }}></span>
                    {
                      // if structure type is slot
                      isEditableCell(idx, child.key) ? (
                        <Input
                          value={editableValue}
                          onChangeText={(value) =>
                            onEditableValueChange({
                              rowData: item,
                              rowIndex: idx,
                              column: child,
                              value,
                            })
                          }
                          onBlur={() => deactivateEditableCell()}
                        />
                      ) : item?.empty?.indexOf(child.body) > -1 ? (
                        <View></View>
                      ) : child.type == 'slot' &&
                        slotComponent &&
                        slotComponent[child.body] ? (
                        slotComponent[child.body]({
                          data: item,
                          index: idx,
                        })
                      ) : child.type == 'status' &&
                        getItem(item, child.body) ? (
                        // if structure type is status
                        <View style={isWeb ? { ...styles.centerColumn } : {}}>
                          <StatusBadge status={getItem(item, child.body)} />
                        </View>
                      ) : child.type == 'status_active' ? (
                        // if structure type is status of true or false
                        <View style={isWeb ? { ...styles.centerColumn } : {}}>
                          <StatusBadge
                            status={
                              getItem(item, child.body) ? 'active' : 'in_active'
                            }
                          />
                        </View>
                      ) : child.type == 'image' ? (
                        // if structure type is image
                        <Image
                          style={{
                            width: child?.style?.width ? child.style.width : 50,
                            height: child?.style?.height
                              ? child.style.height
                              : 50,
                          }}
                          width={child?.style?.width ? child.style.width : 50}
                          compressWidth={
                            child?.style?.width ? child.style.width : 50
                          }
                          height={
                            child?.style?.height ? child.style.height : 50
                          }
                          source={getItem(item, child.body)}
                        />
                      ) : child.type == 'imageClick' ? (
                        // if structure type is image can be click open new tab
                        <TouchableOpacity
                          onPress={() => {
                            if (isWeb) {
                              window.open(item?.url ? item?.url : '', '_blank');
                            }
                          }}>
                          <View
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                            <Image
                              style={{
                                width: child?.style?.width
                                  ? child.style.width
                                  : 50,
                                height: child?.style?.height
                                  ? child.style.height
                                  : 50,
                              }}
                              width={
                                child?.style?.width ? child.style.width : 50
                              }
                              compressWidth={
                                child?.style?.width ? child.style.width : 50
                              }
                              height={
                                child?.style?.height ? child.style.height : 50
                              }
                              source={getItem(item, child.body)}
                            />
                          </View>
                        </TouchableOpacity>
                      ) : child.type == 'checkbox' ? (
                        // if structure type is checkbox form
                        <View key={`checkbox-${idx}`}>
                          <CheckBox
                            value={idx}
                            checked={
                              checkbox.findIndex((cb) => cb.index == idx) > -1
                            }
                            checkboxStyle={{ width: 16, height: 16 }}
                            onChange={() => onCheckbox(item, idx)}
                          />
                        </View>
                      ) : child.type == 'action' ? (
                        // if structure type is action and it will rendering some buttons
                        <View
                          style={{
                            flexWrap: 'wrap',
                            flexDirection: 'row',
                          }}>
                          {(child.actions || []).map(
                            (actionItem, actionIdx) => (
                              <TouchableOpacity
                                key={`action-${actionIdx}`}
                                style={{
                                  paddingHorizontal: 6,
                                  paddingVertical: 4,
                                  borderRadius: 4,
                                  borderWidth: 1,
                                  borderColor: Colors.grey0,
                                  backgroundColor: Colors.grey0,
                                  marginRight: 5,
                                  marginBottom: 5,
                                  ...(actionItem.style &&
                                  Object.keys(actionItem.style).length
                                    ? actionItem.style
                                    : {}),
                                }}
                                activeOpacity={0.6}
                                onPress={() =>
                                  onMapAction(item, idx, actionItem)
                                }>
                                <SociollaText
                                  type="medium"
                                  style={{
                                    color: Colors.black,
                                    fontSize: 11,
                                    ...(actionItem.textStyle &&
                                    Object.keys(actionItem.textStyle).length
                                      ? actionItem.textStyle
                                      : {}),
                                  }}>
                                  {actionItem.label == 'INACTIVE' &&
                                  !item.is_active
                                    ? 'ACTIVE'
                                    : actionItem.label}{' '}
                                  {/*handling for users setting page, if user active then show 'INACTIVE' button  */}
                                </SociollaText>
                              </TouchableOpacity>
                            ),
                          )}
                        </View>
                      ) : child.type == 'action_icon' ? (
                        // if structure type is action and it will rendering some buttons
                        <View
                          style={{
                            flexWrap: 'wrap',
                            flexDirection: 'row',
                          }}>
                          {(child.actions || []).map(
                            (actionItem, actionIdx) => (
                              <TouchableOpacity
                                key={`action-${actionIdx}`}
                                style={{
                                  paddingHorizontal: 6,
                                  paddingVertical: 4,
                                  marginRight: 5,
                                  marginBottom: 5,
                                  ...(actionItem.style &&
                                  Object.keys(actionItem.style).length
                                    ? actionItem.style
                                    : {}),
                                }}
                                activeOpacity={0.6}
                                onPress={() =>
                                  onMapAction(item, idx, actionItem)
                                }>
                                {actionItem.name == 'delete' ? (
                                  <Image
                                    style={{
                                      width: trash.width,
                                      height: trash.height,
                                    }}
                                    width={trash.width}
                                    height={trash.height}
                                    source={require('@assets/img/icons/trash-grey.svg')}
                                    svg={true}
                                  />
                                ) : actionItem.icon ? (
                                  <Image
                                    style={{
                                      width: iconSize,
                                      height: iconSize,
                                    }}
                                    width={iconSize}
                                    height={iconSize}
                                    source={actionItem.icon}
                                    svg={true}
                                  />
                                ) : (
                                  <Image
                                    style={{
                                      width: iconSize,
                                      height: iconSize,
                                    }}
                                    width={iconSize}
                                    height={iconSize}
                                    source={require('@assets/img/icons/edit-blue.svg')}
                                    svg={true}
                                  />
                                )}
                              </TouchableOpacity>
                            ),
                          )}
                        </View>
                      ) : child.type == 'date' ? (
                        // if structure type is date format
                        renderCellText({
                          rowData: item,
                          structure: child,
                          textStyle,
                          formatter: (value) =>
                            parseDate(
                              value,
                              child.format ? child.format : 'dd MMM yyyy',
                            ),
                        })
                      ) : child.type == 'datetime' ? (
                        // if structure type is date time format
                        renderCellText({
                          rowData: item,
                          structure: child,
                          textStyle,
                          formatter: (value) =>
                            parseDate(
                              value,
                              child.format ? child.format : 'dd MMM yyyy HH:mm',
                            ),
                        })
                      ) : child.type == 'currency' ? (
                        // if structure type is currency format
                        isEdit && child.body == 'budget_sample' ? (
                          <Input
                            isNumeric={true}
                            value={getItem(item, child.body)}
                            onChangeText={(value) =>
                              ChangeBudgetSample(value, item)
                            }
                          />
                        ) : (
                          renderCellText({
                            rowData: item,
                            structure: child,
                            textStyle,
                            formatter: (value) => currency(value, true),
                          })
                        )
                      ) : child.type == 'currency-number' ? (
                        // if structure type is currency format
                        renderCellText({
                          rowData: item,
                          structure: child,
                          textStyle,
                          formatter: (value) => currency(value, false),
                        })
                      ) : child.type == 'percentage' ? (
                        renderCellText({
                          rowData: item,
                          structure: child,
                          textStyle,
                          formatter: (value) => `${value}% `,
                        })
                      ) : (
                        // if have no structure type (it will be render as default text)
                        renderCellText({
                          rowData: item,
                          structure: child,
                          textStyle,
                        })
                      )
                    }
                  </td>
                ) : (
                  false
                );
              })}
              {
                // Handling hide/show column
                hideColumn && hideColumn.length && !hasNoHide ? (
                  <td></td>
                ) : (
                  false
                )
              }
            </tr>
          );
        })}
      </>
    ) : (
      // Empty result of table
      <tr>
        <td colSpan={finalStructures.length} style={{ padding: 15 }}>
          <TableEmpty
            hasFilter={hasFilter}
            showResetFilter={showResetFilter}
            onResetFilter={onResetFilter}
          />
        </td>
      </tr>
    );
  },
  (prev, next) => prev == next,
);

const BottomPagination = React.memo(
  (props) => {
    const {
      testID,
      showPagination,
      hideBottomPagination,
      subTotal,
      totalData,
      skip,
      limit,
      onSetSkip,
      onSetLimit,
      overflowX,
    } = props;

    return (showPagination ? (hideBottomPagination ? false : true) : false) ? (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          ...(overflowX
            ? {
                position: 'sticky',
                left: 0,
                zIndex: 101,
                elevation: 101,
              }
            : {}),
        }}>
        <View>
          {subTotal ? (
            <SociollaText
              testID={`${testID}-subtotal`}
              type="medium"
              style={{
                fontSize: 13,
                lineHeight: 15,
                marginTop: 1,
                marginRight: 20,
                color: Colors.grey1,
              }}>
              Subtotal : {currency(subTotal, true)}
            </SociollaText>
          ) : (
            false
          )}
        </View>
        <View style={{ marginLeft: 'auto' }}>
          <Pagination
            testID={testID}
            totalData={totalData}
            limit={limit}
            skip={skip}
            dropUp={true}
            onSetSkip={(val) => onSetSkip(val)}
            onSetLimit={onSetLimit ? (val) => onSetLimit(val) : null}
          />
        </View>
      </View>
    ) : (
      false
    );
  },
  (prev, next) => prev == next,
);

const Table = (props) => {
  const dispatch = useDispatch();
  const { isEdit, ChangeBudgetSample } = props;
  const filterName = props.filterName ? props.filterName : null;
  const paramsState = useSelector(selectParams(filterName)); // existing parameters for filter
  const showSearch = props.showSearch ?? true; // show/hide search input on table
  const searchKey = props.searchKey ? props.searchKey : null; // search key for filter
  const name = props.name ? props.name : true; // table name (this will used for showing filter based on name table and showFilter value)
  const showFilter = props.showFilter == false ? false : true; // show or hide filter on table
  const showPagination = props.showPagination == false ? false : true; // show or hide pagination on table
  const withFetch = props.withFetch == false ? false : true; // has fetch process on outside table component
  const withSort = props.withSort ? props.withSort : false; // has sort process on table
  const hideBottomPagination = props.hideBottomPagination || false; // show or hide botton pagination only
  const bottomPaginationOnly = props.bottomPaginationOnly ? true : false; // show or hide botton pagination only
  const showResetFilter = props.showResetFilter == false ? false : true;
  const moreInlineFilter = props.moreInlineFilter
    ? props.moreInlineFilter
    : null;
  const structures =
    props.structures && Object.keys(props.structures) ? props.structures : {}; // data structures of table
  const loading = props.loading ? props.loading : false; // first-load (no data yet) → full spinner
  const refetching = props.refetching ? true : false; // background refetch (search/paginate/filter) → keep data + overlay
  const busy = loading || refetching; // block new search/paginate/sort/reset actions while a fetch is in flight
  const totalData = props.totalData ? props.totalData : 0; // total data table
  const limit = props.limit ? props.limit : 10; // limit of table
  const skip = props.skip ? props.skip : 0; // skip for pagination table
  const data = props.data || []; // data of the table
  const searchWidth = props.searchWidth ? props.searchWidth : 0;
  const hideLimitOptions = props.hideLimitOptions ? true : false; // Hides the "Rows per page" control
  const noRowColor = props.noRowColor ? true : false;
  const rowBottomBorder = props.rowBottomBorder ? true : false;
  const headerComponent = props.headerComponent ? props.headerComponent : false;
  const subTotal = props.subTotal ? props.subTotal : 0;
  const overflowX = props.overflowX ? true : false;
  const finalStructures = [];
  const finalTopStructures = [];
  const headerTopStructures = [];
  const headerBottomStructures = [];
  const stickyLeftByKey = {};
  const stickyWidthByKey = {};
  const [checkbox, setCheckbox] = useState([]);
  const [hideColumn, setHideColumn] = useState([]);
  const [showMoreField, setShowMoreField] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState(
    props.searchValue ? props.searchValue : '',
  );
  const [searchForKeyword, setSearchForKeyword] = useState(
    props.searchForValue ? props.searchForValue : '',
  );
  const customTopSection = props.customTopSection
    ? props.customTopSection
    : false;
  const noTopSection = props.noTopSection ? props.noTopSection : false;
  const searchBorder = props.searchBorder ? props.searchBorder : false;
  const searchPlaceholder = props.searchPlaceholder
    ? props.searchPlaceholder
    : false;
  const hasNoHide = props.hasNoHide ? props.hasNoHide : false;
  const slotComponent = props.slot ? props.slot : false;
  const searchForOptions = props.searchForOptions
    ? props.searchForOptions
    : false;
  const disablePointer = props.disablePointer ? props.disablePointer : false;

  const hasSort = paramsState.sort;
  const hasFilter =
    paramsState.filter &&
    Object.keys(paramsState.filter).filter((a) => {
      if (!searchKey) {
        return true;
      }
      if (Array.isArray(searchKey)) {
        return a !== '$or';
      }
      return a !== searchKey;
    }).length > 0;

  if (!(structures && Object.keys(structures).length)) {
    // if have no data structures
    return;
  } else {
    // if the table have a data structures
    const tempHide = [];
    const keys = Object.keys(structures);
    const hasTopHeader = keys.some((key) => structures[key]?.top);
    let stickyOffset = 0;
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const item = structures[key];
      if (item.top) {
        finalTopStructures.push({ key: key, ...item });
        headerTopStructures.push({ key: key, ...item });
      } else {
        finalStructures.push({ key: key, ...item });
        if (hasTopHeader && Number(item.rowSpan || 1) > 1) {
          headerTopStructures.push({ key: key, ...item });
        } else {
          headerBottomStructures.push({ key: key, ...item });
        }
      }
      if (item && item.hide) {
        tempHide.push({
          name: item.head,
          value: key,
          show: false,
        });
      }

      if (item && item.sticky) {
        const stickyWidth = Number(
          item.stickyWidth || item.width || item.maxWidth || 180,
        );
        stickyWidthByKey[key] = stickyWidth;
        stickyLeftByKey[key] = stickyOffset;
        stickyOffset += stickyWidth;
      }
    }
    if (
      !hasNoHide &&
      !(hideColumn && hideColumn.length) &&
      hideColumn.length != tempHide.length
    ) {
      setHideColumn(tempHide);
    }
  }

  // check the columns should be show or not
  const isShowHide = (key) =>
    Boolean(
      hasNoHide ||
        (hideColumn &&
          hideColumn.length &&
          hideColumn.findIndex(
            (m) =>
              m.value == key &&
              (Object.prototype.hasOwnProperty.call(m, 'show')
                ? !m.show
                : true),
          ) == -1),
    );

  // handling show/hide column
  const showField = (idx) => {
    const tempField = JSON.parse(JSON.stringify(hideColumn));
    try {
      tempField[idx].show = !tempField[idx].show;
    } catch (error) {}
    setHideColumn(tempField);
  };

  // callback when clicking row
  const onClickRow = ({ data, index }) => {
    try {
      if (props.hasCallback) {
        props.onClickRow({ data, index });
      }
    } catch (error) {}
  };

  // callback for sorting or custom action
  const onMapAction = (item, idx, action) => {
    try {
      if (action.name == 'sort') {
        if (busy) {
          return;
        }
        if (withSort) {
          if (withFetch) {
            const newSort = `${
              hasSort && hasSort.indexOf('-') > -1 ? '' : '-'
            }${item.body}`;
            dispatch(
              paginationAdded({ key: filterName, skip: 0, limit: limit }),
            );
            dispatch(
              sortChanged({
                key: filterName,
                sort: newSort,
              }),
            );
          }
          props.onSearch?.();
        }
      } else {
        props.onMapAction({
          data: item,
          index: idx,
          action: action,
        });
      }
    } catch (error) {}
  };

  // get item value from data based on data structures
  const getItem = (item, key) => {
    if (key && key.indexOf('.') > -1) {
      try {
        return key.split('.').reduce((o, i) => o[i], item);
      } catch (error) {
        return item[key];
      }
    }
    return item[key];
  };

  // handling checkbox callback
  const onCheckbox = (item, idx = -1, is_all = false) => {
    try {
      let newCheckbox = [];
      if (data && data.length) {
        if (is_all) {
          if (checkbox?.length && checkbox.length == (data && data.length)) {
            newCheckbox = [];
          } else {
            const temp = [];
            for (let i = 0; i < data.length; i++) {
              const cb = data[i];
              temp.push({ ...cb, index: i });
            }
            newCheckbox = temp;
          }
        } else if (idx > -1) {
          if (
            checkbox?.length &&
            checkbox.findIndex((cb) => cb.index == idx) > -1
          ) {
            const checkIdx = checkbox.findIndex((cb) => cb.index == idx);
            const tempChecked = JSON.parse(JSON.stringify(checkbox));
            tempChecked.splice(checkIdx, 1);
            newCheckbox = tempChecked;
          } else {
            const checked = JSON.parse(JSON.stringify(checkbox));
            checked.push({ ...item, index: idx });
            newCheckbox = checked;
          }
        }
      }
      setCheckbox(newCheckbox);
      props.onCheckbox(newCheckbox);
    } catch (error) {}
  };

  // on search callback
  const onSearch = (val, forValue) => {
    if (busy) {
      return;
    }
    try {
      if (checkbox?.length) {
        onCheckbox({}, -1, true);
      }
      if (withFetch) {
        const oldParams = getCurrentParams(store.getState(), filterName);
        const oldFilter = oldParams?.filter || {};
        const new_filter = {
          ...oldFilter,
          search_for: forValue,
        };
        if (Array.isArray(searchKey)) {
          if (val) {
            new_filter.$or = searchKey.map((field) => ({
              [field]: { $regex: val, $options: 'i' },
            }));
          } else {
            delete new_filter.$or;
          }
        } else {
          new_filter[searchKey] = val;
          if (!val) {
            delete new_filter[searchKey];
          }
        }
        if (!forValue) {
          delete new_filter.search_for;
        }
        dispatch(paginationAdded({ key: filterName, skip: 0, limit: limit }));
        dispatch(filterAdded({ key: filterName, filter: new_filter }));
        setSavedFilter({ filters: new_filter });
      }
    } catch (error) {}
    props.onSearch?.(val, forValue);
  };

  // callback when do change the pagination
  const onSetSkip = (val) => {
    if (busy) {
      return;
    }
    if (checkbox?.length) {
      onCheckbox({}, -1, true);
    }
    if (withFetch) {
      dispatch(paginationAdded({ key: filterName, skip: val, limit: limit }));
    }
    if (props.onLoadMore) {
      props.onLoadMore(val);
    } else {
      props.onSearch?.(searchKeyword, searchForKeyword);
    }
  };

  // callback when changing the rows per page (resets to first page)
  const onSetLimit = (val) => {
    if (busy) {
      return;
    }
    if (checkbox?.length) {
      onCheckbox({}, -1, true);
    }
    if (withFetch) {
      dispatch(paginationAdded({ key: filterName, skip: 0, limit: val }));
    }
    if (props.onSetLimit) {
      props.onSetLimit(val);
    } else {
      props.onSearch?.(searchKeyword, searchForKeyword);
    }
  };

  // reset all filter and pagination
  const onResetFilter = () => {
    if (busy) {
      return;
    }
    if (withFetch) {
      dispatch(paramsReseted({ key: filterName }));
    }
    props.onSearch?.();
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    setSearchKeyword(props.searchValue);
    return () => {
      setSearchKeyword('');
    };
  }, [props.searchValue]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    setSearchForKeyword(props.searchForValue);
    return () => {
      setSearchForKeyword('');
    };
  }, [props.searchForValue]);

  return (
    <View
      testID={props.testID || 'table'}
      style={{ width: '100%', ...(overflowX ? { overflowX: 'auto' } : {}) }}>
      <TableTop
        testID={props.testID}
        name={name}
        customTopSection={customTopSection}
        noTopSection={noTopSection}
        showSearch={showSearch}
        searchKey={searchKey}
        searchKeyword={searchKeyword}
        searchWidth={searchWidth}
        searchBorder={searchBorder}
        searchPlaceholder={searchPlaceholder}
        showFilter={showFilter}
        hasFilter={hasFilter}
        moreInlineFilter={moreInlineFilter}
        showPagination={showPagination}
        bottomPaginationOnly={bottomPaginationOnly}
        totalData={totalData}
        skip={skip}
        limit={limit}
        dispatch={dispatch}
        setSearchKeyword={setSearchKeyword}
        onSearch={onSearch}
        onSetSkip={onSetSkip}
        onSetLimit={hideLimitOptions ? null : onSetLimit}
        searchForOptions={searchForOptions}
        searchForKeyword={searchForKeyword}
        setSearchForKeyword={setSearchForKeyword}
        overflowX={overflowX}
        filterName={filterName}
      />
      {/* Start Table Container */}
      <View style={{ position: 'relative' }}>
        <table
          style={{
            width: '100%',
            backgroundColor: Colors.white,
            borderCollapse: 'collapse',
            marginTop: 15,
            marginBottom: 15,
            boxShadow: props.withoutShadow
              ? 'none'
              : '3px 1px 15px 1px rgba(0,0,0,0.15)',
          }}>
          {/* Start Table Header Container */}
          <thead>
            {finalTopStructures.length ? (
              <tr style={{ backgroundColor: Colors.blue3 }}>
                <TableHeader
                  isTop
                  finalStructures={headerTopStructures}
                  hideColumn={hideColumn}
                  hasNoHide={hasNoHide}
                  data={data}
                  checkbox={checkbox}
                  withSort={withSort}
                  hasSort={hasSort}
                  isShowHide={isShowHide}
                  onCheckbox={onCheckbox}
                  onMapAction={onMapAction}
                  stickyLeftByKey={stickyLeftByKey}
                  stickyWidthByKey={stickyWidthByKey}
                />
              </tr>
            ) : (
              false
            )}
            <tr style={{ backgroundColor: Colors.blue3 }}>
              <TableHeader
                finalStructures={
                  finalTopStructures.length
                    ? headerBottomStructures
                    : finalStructures
                }
                hideColumn={hideColumn}
                hasNoHide={hasNoHide}
                data={data}
                checkbox={checkbox}
                withSort={withSort}
                hasSort={hasSort}
                isShowHide={isShowHide}
                onCheckbox={onCheckbox}
                onMapAction={onMapAction}
                stickyLeftByKey={stickyLeftByKey}
                stickyWidthByKey={stickyWidthByKey}
              />
              {/* Handling hide/show column */}
              <TableHideColumn
                hideColumn={hideColumn}
                hasNoHide={hasNoHide}
                showMoreField={showMoreField}
                setShowMoreField={setShowMoreField}
                showField={showField}
              />
            </tr>
          </thead>
          {/* End Table Header Container */}
          {/* Start Table Header Container */}
          <tbody>
            <TableList
              testID={props.testID}
              loading={loading}
              finalStructures={finalStructures}
              data={data}
              hideColumn={hideColumn}
              hasNoHide={hasNoHide}
              headerComponent={headerComponent}
              headerComponentElement={headerComponent?.component}
              noRowColor={noRowColor}
              rowBottomBorder={rowBottomBorder}
              checkbox={checkbox}
              hasFilter={hasFilter}
              showResetFilter={showResetFilter}
              slotComponent={slotComponent}
              onClickRow={onClickRow}
              isShowHide={isShowHide}
              getItem={getItem}
              onCheckbox={onCheckbox}
              onMapAction={onMapAction}
              onResetFilter={onResetFilter}
              isEdit={isEdit}
              ChangeBudgetSample={ChangeBudgetSample}
              stickyLeftByKey={stickyLeftByKey}
              stickyWidthByKey={stickyWidthByKey}
              disablePointer={disablePointer}
            />
          </tbody>
          {/* End Table Header Container */}
        </table>
        {/* Keep data visible during a background refetch, dim it + show a spinner overlay */}
        {refetching && !loading && data && data.length ? (
          <View style={styles.refetchOverlay}>
            <ActivityIndicator size="large" color={Colors.blue0} />
          </View>
        ) : null}
      </View>
      {/* End Table Container */}
      {/* Bottom Pagination Container */}
      <BottomPagination
        testID={`${props.testID}-bottom-pagination`}
        showPagination={showPagination}
        hideBottomPagination={hideBottomPagination}
        subTotal={subTotal}
        totalData={totalData}
        skip={skip}
        limit={limit}
        onSetSkip={onSetSkip}
        onSetLimit={hideLimitOptions ? null : onSetLimit}
        overflowX={overflowX}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  centerColumn: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  refetchOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: Colors.whiteOverlay,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
});

export default React.memo(Table, (prev, next) => prev == next);
